'use client'

// oxlint-disable-next-line cf-web/banned-imports
import { SpinnerIcon } from 'primitives/icons'
import { ChevronRightIcon } from '@repo/studio-design-system-ui-icons/arrows'
import { HeartIcon } from '@repo/studio-design-system-ui-icons/general'
import { LayoutAlt02Icon } from '@repo/studio-design-system-ui-icons/layout'
import * as React from 'react'
import { useIsLoggedInQuery } from 'auth/hooks'
import { tw } from 'utils/classnames'
import type { MenuItem } from 'instantsearch.js/es/connectors/menu/connectMenu'
import { useInstantSearch } from 'react-instantsearch'
import { algoliaTemplatesIndex } from 'algolia/algolia-templates'

import { useTemplatesPageContext } from '../context/templates-page'
import { MenuRowWebsiteMenu } from 'components/menu-row-website-menu'
import { useUserAuthModal } from '@repo/user-auth-dialog/user-auth-dialog-context'
import { ServerOnly } from 'components/client-only'

function MenuSkeleton() {
  const lengths = ['w-[50%]', 'w-[70%]', 'w-[40%]']

  return lengths.map(length => (
    <MenuRowWebsiteMenu
      variant="template-second"
      key={length}
      className="pointer-events-none animate-pulse bg-gray-900"
    >
      <MenuRowWebsiteMenu.RightIcon>
        <ChevronRightIcon />
      </MenuRowWebsiteMenu.RightIcon>
      <div className={`${length} h-4 rounded bg-gray-800`} />
    </MenuRowWebsiteMenu>
  ))
}

const NavigationBarContext = React.createContext<{
  attribute: 'category' | 'subcategory'
  selectedCategory: string
  query: string
  isFavorites: boolean
  onRefine: (value: string, link?: string) => void
} | null>(null)

function useNavigationBarContext() {
  const data = React.useContext(NavigationBarContext)

  if (!data) {
    throw new Error('')
  }

  return data
}

interface NavigationBarTemplatesProp {
  isFavorites?: boolean
  isAllTemplates?: boolean
  attribute: 'category' | 'subcategory'
  onRefine: (value: string) => void
  children?: React.ReactNode
}
function Root({
  isFavorites: isFavoritesProp = false,
  attribute,
  onRefine,
  children,
}: NavigationBarTemplatesProp) {
  const { templatesGalleryRoute } = useTemplatesPageContext()
  const { uiState } = useInstantSearch()
  const selectedCategory = uiState?.[algoliaTemplatesIndex]?.menu?.[attribute] || ''
  const query = uiState?.[algoliaTemplatesIndex]?.query || ''

  const isSSR = typeof window === 'undefined'
  const isFavoritesRoute =
    !isSSR && window.location.pathname.includes(`${templatesGalleryRoute}favorites`)

  return (
    <NavigationBarContext.Provider
      value={{
        attribute,
        selectedCategory,
        query,
        // During SSR we rely on the prop to determine if we are on the favorites page
        // Client-side, we check the URL as the prop may not change after navigation
        isFavorites: isSSR ? isFavoritesProp : isFavoritesRoute,
        onRefine,
      }}
    >
      <div className="flex w-full flex-col">{children}</div>
    </NavigationBarContext.Provider>
  )
}

interface MenuHeaderButtonProps {
  allTitle: string
  selectedClassName?: string
}
function MenuHeaderButton({ allTitle, selectedClassName }: MenuHeaderButtonProps) {
  const { templatesGalleryRoute } = useTemplatesPageContext()
  const { isFavorites, selectedCategory, onRefine } = useNavigationBarContext()

  const selected = Boolean(!isFavorites && (selectedCategory === '' || selectedCategory === null))

  return (
    <MenuRowWebsiteMenu.Link
      selected={selected}
      selectedClassName={selectedClassName}
      variant="template"
      onClick={isFavorites ? undefined : () => onRefine('', templatesGalleryRoute)}
      href={templatesGalleryRoute}
    >
      <MenuRowWebsiteMenu.Icon>
        <LayoutAlt02Icon />
      </MenuRowWebsiteMenu.Icon>
      {allTitle}
    </MenuRowWebsiteMenu.Link>
  )
}

interface MenuFavoritesButtonProps {
  title: string
  selectedClassName?: string
}
function MenuFavoritesButton({ title, selectedClassName }: MenuFavoritesButtonProps) {
  const { templatesGalleryRoute } = useTemplatesPageContext()
  const { isFavorites } = useNavigationBarContext()
  const isLoggedInQuery = useIsLoggedInQuery()
  const { openModal } = useUserAuthModal()

  return (
    <MenuRowWebsiteMenu.Link
      selected={isFavorites}
      variant="template"
      href={`${templatesGalleryRoute}favorites`}
      onClick={isLoggedInQuery.isPending || isLoggedInQuery.data ? undefined : () => openModal()}
      selectedClassName={selectedClassName}
    >
      <MenuRowWebsiteMenu.Icon>
        <HeartIcon />
      </MenuRowWebsiteMenu.Icon>
      {title}
    </MenuRowWebsiteMenu.Link>
  )
}

interface MenuItemsProps {
  items: MenuItem[]
  selectedClassName?: string
}
function MenuItems({ items, selectedClassName }: MenuItemsProps) {
  const { templatesGalleryRoute } = useTemplatesPageContext()
  const { isFavorites, attribute, selectedCategory, query, onRefine } = useNavigationBarContext()

  return (
    <div className="relative min-h-10">
      {items.length === 0 && query === '' ? (
        <SpinnerIcon className="m-auto mt-2 size-5 animate-spin text-gray-300" />
      ) : null}
      <ServerOnly>{items.length === 1 ? <MenuSkeleton /> : null}</ServerOnly>

      {items.map(item => {
        const isSelected = selectedCategory === item.value
        const link = `${
          attribute === 'category' ? templatesGalleryRoute : `${templatesGalleryRoute}all/`
        }${item.value}`

        return (
          <MenuRowWebsiteMenu.Link
            key={item.value}
            selectedClassName={selectedClassName}
            selected={isSelected}
            onClick={isFavorites ? undefined : () => onRefine(item.value, link)}
            variant="template-second"
            href={`${
              attribute === 'category' ? templatesGalleryRoute : `${templatesGalleryRoute}all/`
            }${item.value}`}
          >
            <MenuRowWebsiteMenu.RightIcon>
              <ChevronRightIcon
                className={tw(
                  'rotate-0 transform transition-transform duration-300',
                  isSelected && 'rotate-90',
                )}
              />
            </MenuRowWebsiteMenu.RightIcon>
            <p className="truncate font-medium capitalize">{item.value}</p>
          </MenuRowWebsiteMenu.Link>
        )
      })}

      <ServerOnly>{items.length === 1 ? <MenuSkeleton /> : null}</ServerOnly>
    </div>
  )
}

export const NavigationBarTemplates = {
  Root,
  MenuHeaderButton,
  MenuFavoritesButton,
  MenuItems,
}
